import { createContext, useContext, useEffect } from "react";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { ContractAcceptanceModal } from "@/components/ContractAcceptanceModal";
import { TemporaryPasswordModal } from "@/components/TemporaryPasswordModal";
import { FileClock } from "lucide-react";

const ContractContext = createContext(null);
export const useContractStatus = () => useContext(ContractContext);

export const ContractGate = ({ children }) => {
  const { user, logout, contractStatus: status, contractLoading: loading,
    contractError: error, refreshContractStatus: reload, completeContractAcceptance } = useAuth();
  const needsPreview = !status || (status.needs_acceptance && status.is_signer && !status.rendered_html);
  useEffect(() => {
    // An already checked session keeps its result through every route change.
    // Only restored legacy tokens or an unfinished signer preview need a read.
    if (user?.id && needsPreview) {
      reload();
    }
  }, [user?.id, needsPreview, reload]);

  if (loading || !status && !error) return <div className="min-h-screen flex items-center justify-center text-sm text-[#707070]" data-testid="contract-status-loading">Caricamento contratto…</div>;
  if (error) return <div className="min-h-screen flex flex-col items-center justify-center gap-5 px-5" data-testid="contract-status-error">
    <p role="alert">Impossibile verificare il contratto. Riprova tra poco.</p>
    <div className="flex gap-3">
      <Button onClick={reload} data-testid="contract-status-retry">Riprova</Button>
      <Button variant="outline" onClick={logout} data-testid="contract-status-logout">Esci</Button>
    </div>
  </div>;
  if (status.needs_acceptance && status.is_signer) return <ContractAcceptanceModal
    contract={status} onAccepted={completeContractAcceptance} onReload={reload} />;
  if (user?.force_password_change) return <TemporaryPasswordModal />;
  return <ContractContext.Provider value={status}>{children}</ContractContext.Provider>;
};

export const ContractUpdateBanner = () => {
  const status = useContractStatus();
  if (!status?.needs_acceptance || status.is_signer) return null;
  return <div role="status" className="shrink-0 border-b border-[#D9B7AC] bg-[#F8ECE7] text-[#672F27] px-5 sm:px-8 py-3.5"
    data-testid="contract-update-banner">
    <div className="max-w-6xl mx-auto flex items-start gap-3 text-sm">
      <FileClock className="w-5 h-5 shrink-0" />
      <span>{status.renewal_sequence ? `Il rinnovo n. ${status.renewal_sequence} richiede una nuova firma` : "Ci sono aggiornamenti nei termini e condizioni di utilizzo da accettare"}. Chiedi all'amministratore dell'account di accettare il contratto v{status.version}.</span>
    </div>
  </div>;
};